import { datePickerFormItemProps } from "@/utils/componentUtils";
import { Form, Input, Modal, Select } from "antd";
import { useEffect } from "react";
import { useTranslation } from "react-i18next";
import { useEnumeration, useUpdateDashboardGoal } from "../../../../../../common/api/queries";
import HiddenInput from "../../../../../../common/components/form/components/HiddenInput";
import { ModalSizes } from "../../../../../../common/constants";
import { setErrorsToForm } from "../../../../../../common/utils/formUtils";
import { validations } from "../../../../../../common/utils/validationUtils";
import { UUID } from "../../../../../../typings/global";
import { EnumerationType } from "../../../../../enumerations/enums";
import { DashboardGoalType } from "../../../../enums";
import { DashboardGoal, UpdateDashboardGoal } from "../../../../types";
import {
  convertDashboardGoalToUpdateObject,
  getGoalActualValue,
  getGoalTargetValueName,
  isCreatedGoal,
} from "../utils";
import { TargetValueFormInput } from "./parts/TargetValueFormInput";

type Props = {
  isOpen: boolean;
  onCancel: () => void;
  goal: DashboardGoal & { id: UUID };
  agentId: string;
  year: number;
};

export const UpdateGoalModal = ({ isOpen, onCancel, goal, agentId, year }: Props) => {
  const { t } = useTranslation();

  const commissionsSettingsLevelsEnums = useEnumeration(EnumerationType.COMMISSIONS_SETTINGS_LEVELS);
  const commissionsSettingsRanksEnums = useEnumeration(EnumerationType.COMMISSIONS_SETTINGS_RANKS);

  const [form] = Form.useForm<UpdateDashboardGoal & Pick<DashboardGoal, "year" | "type">>();

  const updateGoal = useUpdateDashboardGoal();

  const targetValueName = getGoalTargetValueName(goal.type);

  useEffect(() => {
    if (!isOpen || !isCreatedGoal(goal)) {
      return;
    }

    const formData = convertDashboardGoalToUpdateObject(goal);
    form.setFieldsValue(formData);
  }, [isOpen, goal]);

  const handleSubmit = () => {
    form.validateFields().then(({ year, type, ...values }) => {
      updateGoal.mutate(
        { id1: agentId, id2: goal.id, object: values },
        {
          onSuccess: onCancel,
          onError: (error) => {
            setErrorsToForm(form, "dashboard.goals.attrs", error.violations);
          },
        },
      );
    });
  };

  return (
    <Modal
      width={ModalSizes.TINY}
      open={isOpen}
      onCancel={onCancel}
      cancelText={t("common.cancel")}
      okText={t("common.save")}
      confirmLoading={updateGoal.isPending}
      title={t("dashboard.goals.titles.updateGoal")}
      onOk={handleSubmit}
      afterClose={() => form.resetFields()}
      destroyOnHidden
    >
      <Form form={form} layout="vertical" name="updateGoalForm">
        <HiddenInput name="optimisticLockVersion" />

        <Form.Item
          name="year"
          label={t("dashboard.goals.attrs.year")}
          initialValue={year}
          rules={[validations.notNull]}
        >
          <Input disabled />
        </Form.Item>

        <Form.Item name="type" label={t("dashboard.goals.attrs.type")} rules={[validations.notNull]}>
          <Select
            disabled
            options={Object.values(DashboardGoalType).map((type) => ({
              label: t(`dashboard.enums.dashboardGoalType.${type}`),
              value: type,
            }))}
          />
        </Form.Item>

        <Form.Item
          name={targetValueName}
          label={t(`dashboard.goals.attrs.${targetValueName}`)}
          rules={[validations.notNull]}
          {...(targetValueName === "targetDate" ? datePickerFormItemProps : {})}
        >
          <TargetValueFormInput
            goalType={goal.type}
            level={{
              currentValue: goal.actualCommissionsLevel,
              options: commissionsSettingsLevelsEnums,
            }}
            rank={{
              currentValue: goal?.actualCommissionsRank,
              options: commissionsSettingsRanksEnums,
            }}
            year={year}
          />
        </Form.Item>

        <p className="sub-header-info">{getGoalActualValue(goal)}</p>
      </Form>
    </Modal>
  );
};
